import { useState, useEffect } from 'react';
import { hrms } from '../../services/api';
import { theme } from '../../theme';

const INP = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid #E5E7EB`, borderRadius: '0.5rem', fontSize: '0.875rem', color: '#111827', outline: 'none', boxSizing: 'border-box', background: '#fff' };
const LBL = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' };
const FG = { marginBottom: '1rem' };

const STATUS_COLORS = { planned: '#3B82F6', ongoing: '#F97316', completed: '#22C55E', cancelled: '#EF4444' };
const STATUS_BG = { planned: '#EFF6FF', ongoing: '#FFF7ED', completed: '#F0FDF4', cancelled: '#FEF2F2' };
const PARTICIPANT_STATUS_COLORS = { enrolled: '#3B82F6', completed: '#22C55E', cancelled: '#EF4444' };

// ─── Star Rating Component ──────────────────────────────────────────────────

function StarRating({ value, onChange, label, required }) {
  const [hovered, setHovered] = useState(0);
  const labels = ['', 'Poor', 'Below Average', 'Average', 'Good', 'Excellent'];
  return (
    <div style={FG}>
      <label style={LBL}>{label}{required && <span style={{ color: '#EF4444' }}> *</span>}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button"
            onMouseEnter={() => setHovered(n)} onMouseLeave={() => setHovered(0)}
            onClick={() => onChange && onChange(n)}
            style={{ background: 'none', border: 'none', cursor: onChange ? 'pointer' : 'default', fontSize: '1.6rem', padding: '0.1rem', lineHeight: 1, color: n <= (hovered || value) ? '#F97316' : '#E5E7EB', transition: 'color 0.1s' }}>
            ★
          </button>
        ))}
        <span style={{ marginLeft: '0.5rem', fontSize: '0.82rem', color: '#6B7280', minWidth: '90px' }}>
          {hovered ? labels[hovered] : (value ? labels[value] : 'Not rated')}
        </span>
      </div>
    </div>
  );
}

// ─── Feedback Form ──────────────────────────────────────────────────────────

function FeedbackForm({ training, existingFeedback, onSubmitted, onCancel }) {
  const [form, setForm] = useState({
    overall_rating: existingFeedback?.overall_rating || 0,
    trainer_effectiveness: existingFeedback?.trainer_effectiveness || 0,
    content_relevance: existingFeedback?.content_relevance || 0,
    practical_applicability: existingFeedback?.practical_applicability || 0,
    objectives_met: existingFeedback?.objectives_met || 0,
    would_recommend: existingFeedback?.would_recommend ?? true,
    key_learnings: existingFeedback?.key_learnings || '',
    improvements_suggested: existingFeedback?.improvements_suggested || '',
    comments: existingFeedback?.comments || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isReadOnly = !!existingFeedback;

  const handleSubmit = async () => {
    if (!form.overall_rating) { setError('Please provide an overall rating.'); return; }
    setSaving(true); setError('');
    try {
      await hrms.submitTrainingFeedback(training.id, form);
      onSubmitted();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to submit feedback.'); } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: '680px' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1C1C1C 0%, #2D2D2D 100%)', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', color: '#fff' }}>
        <div style={{ fontSize: '0.72rem', color: '#F97316', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '0.3rem' }}>TRAINING FEEDBACK FORM</div>
        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{training.title}</div>
        {training.start_date && <div style={{ fontSize: '0.8rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
          {new Date(training.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
          {training.end_date && ` – ${new Date(training.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`}
        </div>}
        {isReadOnly && <div style={{ marginTop: '0.5rem', background: '#22C55E30', color: '#86EFAC', borderRadius: '0.375rem', padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, display: 'inline-block' }}>✓ Feedback Submitted</div>}
      </div>

      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', color: '#DC2626', fontSize: '0.875rem' }}>{error}</div>}

      {/* Section 1: Overall Ratings */}
      <div style={{ background: '#fff', borderRadius: '0.75rem', border: '1px solid #E5E7EB', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#374151', letterSpacing: '0.08em', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #F3F4F6' }}>
          SECTION 1 — TRAINING EFFECTIVENESS RATINGS
        </div>
        <StarRating label="Overall Training Effectiveness" value={form.overall_rating} onChange={isReadOnly ? null : v => setForm(f => ({...f, overall_rating: v}))} required />
        <StarRating label="Trainer / Facilitator Effectiveness" value={form.trainer_effectiveness} onChange={isReadOnly ? null : v => setForm(f => ({...f, trainer_effectiveness: v}))} />
        <StarRating label="Content Quality & Relevance" value={form.content_relevance} onChange={isReadOnly ? null : v => setForm(f => ({...f, content_relevance: v}))} />
        <StarRating label="Practical Applicability to Your Role" value={form.practical_applicability} onChange={isReadOnly ? null : v => setForm(f => ({...f, practical_applicability: v}))} />
        <StarRating label="Learning Objectives Were Met" value={form.objectives_met} onChange={isReadOnly ? null : v => setForm(f => ({...f, objectives_met: v}))} />
      </div>

      {/* Section 2: Recommendation */}
      <div style={{ background: '#fff', borderRadius: '0.75rem', border: '1px solid #E5E7EB', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#374151', letterSpacing: '0.08em', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #F3F4F6' }}>
          SECTION 2 — RECOMMENDATION
        </div>
        <div style={FG}>
          <label style={LBL}>Would you recommend this training to your colleagues?</label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {[true, false].map(v => (
              <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isReadOnly ? 'default' : 'pointer', fontSize: '0.9rem', fontWeight: form.would_recommend === v ? 700 : 400, color: form.would_recommend === v ? (v ? '#16A34A' : '#DC2626') : '#374151' }}>
                <input type="radio" name="recommend" value={String(v)} checked={form.would_recommend === v} onChange={() => !isReadOnly && setForm(f => ({...f, would_recommend: v}))} disabled={isReadOnly} />
                {v ? '👍 Yes, I would recommend it' : '👎 No, I would not recommend it'}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Section 3: Qualitative Feedback */}
      <div style={{ background: '#fff', borderRadius: '0.75rem', border: '1px solid #E5E7EB', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#374151', letterSpacing: '0.08em', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #F3F4F6' }}>
          SECTION 3 — QUALITATIVE FEEDBACK
        </div>
        <div style={FG}>
          <label style={LBL}>Key Learnings & Takeaways</label>
          <textarea disabled={isReadOnly} style={{ ...INP, minHeight: '80px', resize: 'vertical', background: isReadOnly ? '#F9FAFB' : '#fff' }}
            value={form.key_learnings} onChange={e => setForm(f => ({...f, key_learnings: e.target.value}))}
            placeholder="What were the most valuable things you learned from this training?" />
        </div>
        <div style={FG}>
          <label style={LBL}>Suggested Improvements</label>
          <textarea disabled={isReadOnly} style={{ ...INP, minHeight: '80px', resize: 'vertical', background: isReadOnly ? '#F9FAFB' : '#fff' }}
            value={form.improvements_suggested} onChange={e => setForm(f => ({...f, improvements_suggested: e.target.value}))}
            placeholder="How could this training program be improved?" />
        </div>
        <div style={FG}>
          <label style={LBL}>Additional Comments</label>
          <textarea disabled={isReadOnly} style={{ ...INP, minHeight: '70px', resize: 'vertical', background: isReadOnly ? '#F9FAFB' : '#fff' }}
            value={form.comments} onChange={e => setForm(f => ({...f, comments: e.target.value}))}
            placeholder="Any other comments or suggestions?" />
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '0.6rem 1.25rem', border: '1px solid #E5E7EB', borderRadius: '0.5rem', background: '#fff', color: '#374151', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
          {isReadOnly ? 'Close' : 'Cancel'}
        </button>
        {!isReadOnly && (
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: '0.6rem 1.5rem', border: 'none', borderRadius: '0.5rem', background: saving ? '#FDBA74' : theme.primary, color: '#fff', fontWeight: 700, fontSize: '0.875rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Submitting…' : '📝 Submit Feedback'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Training Card ──────────────────────────────────────────────────────────

function TrainingCard({ training, onFeedback }) {
  const [expanded, setExpanded] = useState(false);
  const trainerLabel = training.trainer_type === 'internal'
    ? `${training.internal_trainer_name || '—'} (Internal)`
    : `${training.trainer || '—'}${training.trainer_company ? ` · ${training.trainer_company}` : ''}`;

  return (
    <div style={{ background: '#fff', borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden', marginBottom: '1rem', transition: 'box-shadow 0.15s' }}>
      {/* Card header */}
      <div style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '1rem' }} onClick={() => setExpanded(v => !v)}>
        <div style={{ width: '44px', height: '44px', borderRadius: '0.5rem', background: `${STATUS_BG[training.status]}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>
          🎓
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: theme.text }}>{training.title}</span>
            <span style={{ background: STATUS_BG[training.status], color: STATUS_COLORS[training.status], borderRadius: '1rem', padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: 700 }}>
              {training.status?.charAt(0).toUpperCase() + training.status?.slice(1)}
            </span>
            {training.category && <span style={{ background: '#F3F4F6', color: '#6B7280', borderRadius: '1rem', padding: '0.15rem 0.5rem', fontSize: '0.68rem', fontWeight: 500 }}>{training.category}</span>}
          </div>
          <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.78rem', color: theme.textMuted, flexWrap: 'wrap' }}>
            {training.start_date && <span>📅 {new Date(training.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
            {training.mode && <span>🖥 {training.mode.charAt(0).toUpperCase()+training.mode.slice(1)}</span>}
            {training.venue && <span>📍 {training.venue}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', flexShrink: 0 }}>
          <span style={{ background: PARTICIPANT_STATUS_COLORS[training.participant_status] + '20', color: PARTICIPANT_STATUS_COLORS[training.participant_status], borderRadius: '1rem', padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: 700 }}>
            {training.participant_status === 'enrolled' ? 'Enrolled' : training.participant_status === 'completed' ? '✓ Completed' : 'Cancelled'}
          </span>
          {training.feedback_submitted && <span style={{ fontSize: '0.7rem', color: '#22C55E', fontWeight: 600 }}>✓ Feedback Given</span>}
          <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${theme.border}` }}>
          <div style={{ paddingTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              ['Trainer', trainerLabel],
              ['Duration', training.start_date && training.end_date ? `${training.start_date} to ${training.end_date}` : training.start_date || '—'],
              ['Mode', training.mode?.charAt(0).toUpperCase()+training.mode?.slice(1)],
              ['Venue', training.venue || '—'],
            ].map(([k,v]) => (
              <div key={k}>
                <div style={{ fontSize: '0.7rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k}</div>
                <div style={{ fontSize: '0.85rem', color: theme.text, fontWeight: 500, marginTop: '0.1rem' }}>{v}</div>
              </div>
            ))}
          </div>
          {training.description && (
            <div style={{ background: '#F9FAFB', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.6 }}>
              {training.description}
            </div>
          )}
          {/* Feedback action */}
          {training.participant_status !== 'cancelled' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => onFeedback(training)}
                style={{ padding: '0.5rem 1.1rem', border: `1px solid ${training.feedback_submitted ? '#22C55E' : theme.primary}`, borderRadius: '0.5rem', background: training.feedback_submitted ? '#F0FDF4' : '#FFF7ED', color: training.feedback_submitted ? '#16A34A' : theme.primary, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                {training.feedback_submitted ? '📋 View My Feedback' : '📝 Give Feedback'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function MyTraining() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackTraining, setFeedbackTraining] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await hrms.getMyTrainings();
      setTrainings(res.data || []);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = trainings.filter(t =>
    (!filterStatus || t.status === filterStatus) &&
    (!search || t.title.toLowerCase().includes(search.toLowerCase()))
  );

  const stats = {
    total: trainings.length,
    completed: trainings.filter(t => t.participant_status === 'completed').length,
    pending_feedback: trainings.filter(t => !t.feedback_submitted && t.status === 'completed').length,
  };

  if (feedbackTraining) {
    return (
      <div>
        <button onClick={() => setFeedbackTraining(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.primary, fontWeight: 600, fontSize: '0.875rem', marginBottom: '1.25rem', padding: 0 }}>
          ← Back to My Trainings
        </button>
        <FeedbackForm
          training={feedbackTraining}
          existingFeedback={feedbackTraining.feedback}
          onSubmitted={() => { load(); setFeedbackTraining(null); }}
          onCancel={() => setFeedbackTraining(null)}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: '0 0 0.25rem' }}>My Training Records</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: 0 }}>View your enrolled trainings and submit feedback</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Enrolled', value: stats.total, color: '#3B82F6', bg: '#EFF6FF' },
          { label: 'Completed', value: stats.completed, color: '#22C55E', bg: '#F0FDF4' },
          { label: 'Feedback Pending', value: stats.pending_feedback, color: '#F97316', bg: '#FFF7ED' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: '0.75rem', padding: '1rem 1.25rem', border: `1px solid ${s.color}20` }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <input placeholder="Search trainings…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...INP, width: '220px' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...INP, width: '150px' }}>
          <option value="">All Status</option>
          {['planned','ongoing','completed','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: theme.textMuted }}>Loading your trainings…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: theme.textMuted }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🎓</div>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>No trainings found</div>
          <div style={{ fontSize: '0.875rem' }}>You haven't been enrolled in any training programs yet.</div>
        </div>
      ) : (
        filtered.map(t => <TrainingCard key={t.id} training={t} onFeedback={setFeedbackTraining} />)
      )}
    </div>
  );
}
